import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getMediaDetails } from '@/services/mediaApi';
import { ApiClientError } from '@/services/apiClient';
import type { MediaDetails, MediaType } from '@/types/streaming';
import type { RootState } from '@/store';

export type MediaKey = `${MediaType}:${number}`;

export type MediaCacheStatus = 'loading' | 'ready' | 'error';

export interface MediaCacheEntry {
  status: MediaCacheStatus;
  data?: MediaDetails;
  error?: string;
}

export interface MediaState {
  byKey: Record<MediaKey, MediaCacheEntry>;
}

export function mediaKey(type: MediaType, id: number): MediaKey {
  return `${type}:${id}` as MediaKey;
}

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

const initialState: MediaState = {
  byKey: {},
};

interface FetchArgs {
  type: MediaType;
  id: number;
}

export const fetchMediaDetails = createAsyncThunk<
  { key: MediaKey; data: MediaDetails },
  FetchArgs,
  { state: { media: MediaState }; rejectValue: { key: MediaKey; message: string } }
>(
  'media/fetchDetails',
  async ({ type, id }, { rejectWithValue }) => {
    const key = mediaKey(type, id);
    try {
      const data = await getMediaDetails(type, id);
      return { key, data };
    } catch (err) {
      return rejectWithValue({ key, message: describeError(err) });
    }
  },
  {
    condition: ({ type, id }, { getState }) => {
      const key = mediaKey(type, id);
      const entry = getState().media.byKey[key];
      if (!entry) return true;
      // Already loading or fetched — skip duplicate dispatches.
      return entry.status === 'error';
    },
  },
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaDetails.pending, (state, action) => {
        const key = mediaKey(action.meta.arg.type, action.meta.arg.id);
        state.byKey[key] = { status: 'loading' };
      })
      .addCase(fetchMediaDetails.fulfilled, (state, action) => {
        state.byKey[action.payload.key] = { status: 'ready', data: action.payload.data };
      })
      .addCase(fetchMediaDetails.rejected, (state, action) => {
        const key = action.payload?.key ?? mediaKey(action.meta.arg.type, action.meta.arg.id);
        state.byKey[key] = {
          status: 'error',
          error: action.payload?.message ?? action.error.message ?? 'Failed to load media',
        };
      });
  },
});

export default mediaSlice.reducer;

export function selectMediaEntry(state: RootState, type: MediaType, id: number): MediaCacheEntry | undefined {
  return state.media.byKey[mediaKey(type, id)];
}
